import { Link } from "react-router-dom";
import Header from "../components/Header";

const SECTIONS = [
  {
    h: "1. Who we are",
    p: [
      `These Terms of Service ("Terms") govern your use of https://priceguard.nz (the "Service"), operated by Aubrae Limited trading as PriceGuard NZ, Christchurch, New Zealand ("we", "us", "our").`,
      `By creating an account or using any part of the Service, you agree to these Terms. If you do not agree, do not use the Service.`,
    ],
  },
  {
    h: "2. What the Service does",
    p: [
      `PriceGuard NZ is an information and notification service for New Zealand consumers. We monitor products you have purchased (or intend to purchase) and alert you when the listed price moves inside a retailer's own published price-protection or price-match window. The Service may also offer a wishlist tracker and, in the future, a Receipt Vault for storing receipts and tracking warranties.`,
      `We are not a retailer. We do not sell products, do not hold stock, and do not process refunds. We surface publicly available pricing information, point you to the retailer's own published policy, and help you draft polite, accurate, retailer-ready emails that you choose to send and sign.`,
      `Consistent with the New Zealand Fair Trading Act 1986, we take care that the information we publish about prices, retailer policies, and refund outcomes is accurate and not misleading. We do not invent "was" prices, exaggerate savings, or imply a refund will be approved.`,
    ],
  },
  {
    h: "3. Not affiliated with retailers",
    p: [
      `PriceGuard NZ is independent. We are not endorsed by, partnered with, sponsored by, or otherwise affiliated with PB Tech, JB Hi-Fi, Harvey Norman, Noel Leeming, Mighty Ape, or any other retailer named or referenced on the Service. Retailer names and logos are used solely for descriptive identification under the principle of nominative fair use.`,
      `Whether a retailer honours a price-protection request is entirely the retailer's decision under their own policy. We do not guarantee any refund, price match, warranty outcome, or response time. Drafts we generate use the retailer's own published policy as the reference point and ask politely, never demand.`,
    ],
  },
  {
    h: "4. Your account",
    p: [
      `You must be at least 16 years old to use the Service. You are responsible for keeping your login credentials secure and for all activity that happens under your account.`,
      `You agree to provide accurate information and to update it if it changes. We may suspend or terminate accounts that breach these Terms, are used for abusive purposes (including spamming retailers), or are used to scrape or resell our data.`,
    ],
  },
  {
    h: "5. Pricing, payments, and renewals",
    p: [
      `The Service has a Free tier with feature limits (currently a 5-item watchlist) and a Pro tier with expanded features. Pro is sold as a one-time charge that extends your Pro access window: $4.99 NZD for 30 days, or $49.00 NZD for 365 days. All amounts are New Zealand dollars and are processed by Stripe.`,
      `Pro access does not auto-renew. When your Pro window ends, your account reverts to Free until you choose to renew. You will never be charged automatically.`,
      `We may grant complimentary Pro access to specific users at our discretion (for example, as part of a launch promotion or to compensate for service issues). Complimentary access has no cash value and may be revoked if the account breaches these Terms.`,
    ],
  },
  {
    h: "6. Refunds",
    p: [
      `Pro purchases are non-refundable once payment has been processed, except where required by the New Zealand Consumer Guarantees Act 1993 or where the Service has been materially defective for an extended period. If you believe you are entitled to a refund, email support@priceguard.nz with your Stripe receipt and we will assess it in good faith within 10 working days.`,
    ],
  },
  {
    h: "7. Acceptable use",
    p: [
      `You agree not to: (a) attempt to access another user's account; (b) scrape, copy, or resell any data from the Service; (c) use the Service to send abusive, threatening, or misleading correspondence to retailers; (d) reverse-engineer, decompile, or interfere with the Service; (e) overload our infrastructure, including by automated scripts beyond normal interactive use.`,
      `Any communication you send to a retailer using a PriceGuard-generated draft is your own communication. You are responsible for its accuracy and tone. We draft polite, retailer-friendly templates that quote the retailer's own published policy by design - do not modify them in ways that misrepresent the policy, exaggerate the price drop, or misrepresent yourself, your purchase, or your circumstances. Misleading conduct toward a retailer may breach the Fair Trading Act 1986 and is grounds for us to suspend your account.`,
    ],
  },
  {
    h: "8. Third-party services",
    p: [
      `The Service relies on third-party platforms - including but not limited to Stripe (payments), Resend (email delivery), Google (optional sign-in), and AI providers accessed via the Emergent Universal LLM Key (price-protection email drafting, AI-powered receipt reading). Your use of those services through PriceGuard NZ is subject to their respective terms.`,
    ],
  },
  {
    h: "9. Intellectual property",
    p: [
      `The Service, its design, code, branding, and editorial content are owned by Aubrae Limited or our licensors. You may not copy, reproduce, or create derivative works without our written permission. The data you add to your own account (your tracked purchases, your wishlist, your receipts when the Vault launches) remains yours - we never claim ownership over your personal data.`,
    ],
  },
  {
    h: "10. Disclaimers and accuracy of pricing",
    p: [
      `The Service is provided on an "as-is" basis. Prices we surface come from publicly available retailer pages and may be wrong, delayed, or temporarily missing due to scraping limitations, retailer site changes, or bot-protection systems. Each price card displays the date and time of our last successful check so you can judge how current it is.`,
      `Final price, stock availability, and product specifications are always set by the retailer. Before claiming a price-protection adjustment, you must verify the current price directly on the retailer's website. The Fair Trading Act 1986 places the duty of accurate price representation on the seller, not on us, but we still take reasonable care to surface pricing fairly and to flag when our data may be stale.`,
      `Where the New Zealand Consumer Guarantees Act 1993 or Fair Trading Act 1986 apply and cannot be excluded, nothing in these Terms limits your statutory rights.`,
    ],
  },
  {
    h: "10A. Your consumer rights under New Zealand law",
    p: [
      `Many New Zealand retailers publish a price-protection or price-match guarantee as part of how they compete. These are voluntary commercial policies set by the retailer - they are separate from your statutory rights. Your statutory rights, which cannot be contracted out of, sit in the Consumer Guarantees Act 1993 (acceptable quality, fitness for purpose, correspondence with description) and the Fair Trading Act 1986 (no misleading or deceptive conduct, accurate price representation).`,
      `PriceGuard NZ helps you exercise both: we read the retailer's own published price-protection policy and surface drops inside that window, and we keep your receipt and warranty trail intact in your Vault so you can lean on the CGA when something fails. We never encourage you to claim outside a retailer's published terms, and we never draft requests that misrepresent the facts of your purchase.`,
      `If you have a wider consumer dispute, free help is available from the Citizens Advice Bureau (cab.org.nz), the Commerce Commission (comcom.govt.nz), and the Disputes Tribunal.`,
    ],
  },
  {
    h: "11. Limitation of liability",
    p: [
      `To the maximum extent permitted by law, our total aggregate liability for any claim arising out of or relating to the Service is limited to the greater of (i) the amount you paid us in the 12 months preceding the claim, or (ii) NZD $50. We are not liable for indirect, incidental, consequential, or special damages, including loss of refunds, loss of profits, or loss of data.`,
    ],
  },
  {
    h: "12. Termination",
    p: [
      `You may close your account at any time by emailing support@priceguard.nz. We may suspend or terminate access to the Service if you breach these Terms. On termination, your right to use the Service ends immediately; clauses that by their nature should survive (intellectual property, disclaimers, limitation of liability, governing law) survive termination.`,
    ],
  },
  {
    h: "13. Changes to these Terms",
    p: [
      `We may update these Terms from time to time. Material changes will be notified via email to your registered address and via a banner on the Service at least 14 days before they take effect, unless legal or security requirements demand a faster change. Continued use of the Service after the change constitutes acceptance.`,
    ],
  },
  {
    h: "14. Governing law",
    p: [
      `These Terms are governed by the laws of New Zealand. You and we submit to the exclusive jurisdiction of the courts of New Zealand for any dispute arising out of or in connection with the Service.`,
    ],
  },
  {
    h: "15. Contact",
    p: [
      `Aubrae Limited - trading as PriceGuard NZ`,
      `Christchurch, New Zealand`,
      `support@priceguard.nz`,
    ],
  },
];

export default function Terms() {
  return (
    <div className="min-h-screen bg-white" data-testid="terms-page">
      <Header />
      <main className="max-w-[900px] mx-auto px-6 lg:px-10 py-16">
        <div className="pg-overline mb-3">Legal</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter">Terms of Service<span className="text-[#002FA7]">.</span></h1>
        <p className="text-[#52525b] mt-4 text-sm font-mono-pg">Last updated: February 2026</p>

        <div className="mt-12 space-y-10">
          {SECTIONS.map((s) => (
            <section key={s.h} data-testid={`terms-section-${s.h.split('.')[0].trim()}`}>
              <h2 className="font-heading text-xl font-bold tracking-tight">{s.h}</h2>
              <div className="mt-3 space-y-3">
                {s.p.map((line) => (
                  <p key={line.slice(0, 60)} className="text-[#0a0a0a] leading-relaxed text-base">{line}</p>
                ))}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-16 border-t border-[#e4e4e7] pt-8 text-sm font-mono-pg text-[#52525b]">
          <Link to="/privacy" className="hover:text-black underline" data-testid="terms-privacy-link">Read the Privacy Policy →</Link>
        </div>
      </main>
    </div>
  );
}
